import { forwardRef, useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'

// Formata um número de centavos como pt-BR: 100000 -> "1.000,00".
function formatCents(cents: number): string {
  const neg = cents < 0
  const abs = Math.abs(cents)
  const intPart = Math.floor(abs / 100)
  const dec = String(abs % 100).padStart(2, '0')
  return `${neg ? '-' : ''}${intPart.toLocaleString('pt-BR')},${dec}`
}

// Campo de valor monetário com máscara "1.000,00". Trabalha em CENTAVOS: cada
// dígito digitado entra pela direita (sem 0 preso, decimais automáticos).
// `value`/`onChange` usam o número em unidades da moeda (ex.: 1000.5).
export interface MoneyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  value: number | null | undefined
  onChange: (value: number) => void
  /** Permite valores negativos (digitar "-" alterna o sinal). */
  allowNegative?: boolean
}

export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, allowNegative = false, placeholder = '0,00', inputMode = 'numeric', ...rest },
  ref,
) {
  const num = Number(value) || 0
  const cents = Math.round(num * 100)
  // sinal negativo "em construção" (quando o valor ainda é 0 mas o usuário digitou "-")
  const [neg, setNeg] = useState(num < 0)
  useEffect(() => {
    if (num !== 0) setNeg(num < 0)
  }, [num])

  const display = cents !== 0 ? formatCents(cents) : allowNegative && neg ? '-' : ''

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value
    const sign = allowNegative ? (raw.match(/-/g)?.length ?? 0) % 2 === 1 : false
    const digits = raw.replace(/\D/g, '')
    const nextCents = digits ? parseInt(digits, 10) : 0
    setNeg(sign)
    onChange(((sign ? -1 : 1) * nextCents) / 100)
  }

  return (
    <Input
      ref={ref}
      type="text"
      inputMode={inputMode}
      value={display}
      placeholder={placeholder}
      onChange={handleChange}
      {...rest}
    />
  )
})
